import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { t } from 'i18next';

import { BaseComponent } from '../BaseComponent';
import { LayoutType } from '../../constants/LayoutConstants';
import { SceneStore } from '../../stores/SceneStore';
import { ViewSettingsStore } from '../../stores/ViewSettingsStore';
import { AppConfigurationStore } from '../../stores/AppConfigurationStore';
import { UIHelper } from '../../utils/UIHelper';
import { GlobalHelper } from '../../utils/GlobalHelper';
import { ArchitectureActions } from '../../actions/ArchitectureActions';
import { SidebarHeader } from '../SidebarHeader';
import { InputBox } from '../InputBox';
import { FloorPlanPopupInTab } from '../overlay/floorplan/FloorPlanPopupInTab';
import { ArchitectureItem } from '../ArchitectureItem';

import core = egr.wcf.core;

interface ArchitectureProps extends React.Props<Architecture> {
    layoutType: LayoutType;
    visible: boolean;
}

interface ArchitectureState {
    filter: string;
    wallSelected: boolean;
    camera: core.view.CameraMode;
    dragMode: boolean;
}

interface ArchitectureButton {
    label: string;
    imgClass: string;
    callback: (event?: React.MouseEvent) => void;
    enabled: boolean;
    active: boolean;
}

export class Architecture extends BaseComponent<ArchitectureProps, ArchitectureState> {
    public componentWillMount(): void {
        this.addStoreListener(SceneStore, this.onChange);
        this.addStoreListener(ViewSettingsStore, this.onChange);
        this.setState({
            wallSelected: SceneStore.wallSelected(),
            filter: '',
            camera: SceneStore.getCameraMode(),
            dragMode: ViewSettingsStore.getState().dragMode
        });
    }

    public componentDidUpdate(): void {
        UIHelper.updateScrollbar(ReactDOM.findDOMNode(this) as Element);
    }

    public render(): JSX.Element | null {
        if (!this.props.visible) {
            return null;
        }
        const items: Array<ArchitectureButton> = [
            {
                label: t('Draw wall'), imgClass: 'img-architecture-wall',
                callback: this.drawWall, enabled: this.allowedCamera(),
                active: this.state.dragMode
            },
            {
                label: t('Opening'), imgClass: 'img-architecture-opening',
                callback: ArchitectureActions.insertOpening, enabled: this.state.wallSelected,
                active: false
            },
            {
                label: t('Door'), imgClass: 'img-architecture-door',
                callback: ArchitectureActions.insertDoor, enabled: this.state.wallSelected,
                active: false
            },
            // {
            //     label: t('Double door'), imgClass: 'img-architecture-doubledoor',
            //     callback: ArchitectureActions.insertDoubleDoor, enabled: this.state.wallSelected,
            //     active: false
            // },
            // {
            //     label: t('Glass door'), imgClass: 'img-architecture-glassdoor',
            //     callback: ArchitectureActions.insertGlassDoor, enabled: this.state.wallSelected,
            //     active: false
            // },
            // {
            //     label: t('Glass front'), imgClass: 'img-architecture-glassfront',
            //     callback: ArchitectureActions.insertGlassFront, enabled: this.state.wallSelected,
            //     active: false
            // },
            {
                label: t('Window'), imgClass: 'img-architecture-window',
                callback: ArchitectureActions.insertWindow, enabled: this.state.wallSelected,
                active: false
            },
            // {
            //     label: t('Double window'), imgClass: 'img-architecture-doublewindow',
            //     callback: ArchitectureActions.insertDoubleWindow, enabled: this.state.wallSelected,
            //     active: false
            // },
            {
                label: t('Pillar round'), imgClass: 'img-architecture-pillarround',
                callback: ArchitectureActions.insertPillarRound, enabled: this.allowedCamera(),
                active: false
            },
            {
                label: t('Pillar square'), imgClass: 'img-architecture-pillarsquare',
                callback: ArchitectureActions.insertPillarSquare, enabled: this.allowedCamera(),
                active: false
            },
            // {
            //     label: t('Radiator'), imgClass: 'img-architecture-radiator',
            //     callback: ArchitectureActions.insertRadiator, enabled: this.state.wallSelected,
            //     active: false
            // },
            // {
            //     label: t('Draw walkways'), imgClass: 'img-architecture-walkways',
            //     callback: this.drawWalkways, enabled: this.allowedCamera(),
            //     active: false
            // }
        ].filter(this.filter);

        const tArchitectureItems: Array<JSX.Element> = items.map((item, index): JSX.Element => {
            return (
                <ArchitectureItem
                    key={index}
                    active={item.active}
                    enabled={item.enabled}
                    onClick={item.callback}
                    label={item.label}
                    imgClass={item.imgClass}
                />
            );
        });

        return (
            <div className="sidebar-tab-container" onClick={GlobalHelper.stopActiveTool}>
                <div className="scrollbar-wcf">
                    <SidebarHeader
                        text={t('Architecture')}
                        buttons={AppConfigurationStore.getState().floorplanAvailable ? [{
                                stdClass: 'header-button-dropdown',
                                element: <FloorPlanPopupInTab key="floorplan-popup" layoutType={this.props.layoutType}/>,
                            }] : []
                        }
                    />
                    <div className={this.contentCSSClass(items.length > 5)}>
                        {items.length > 5 ? <InputBox
                            className="filter-input"
                            defaultValue={this.state.filter}
                            placeholder={t('Filter')}
                            changeCallback={this.setFilter}
                            position="after-header"
                        /> : null}
                        <div className="catalog-items">
                            {AppConfigurationStore.getState().inApp
                                ? <div className="long-button-group">{tArchitectureItems}</div>
                                : tArchitectureItems}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    protected onChange = (): void => {
        this.setState(jQuery.extend(
            {}, this.state,
            {
                wallSelected: SceneStore.wallSelected(),
                camera: SceneStore.getCameraMode(),
                dragMode: ViewSettingsStore.getState().dragMode
            }
        ));
    };

    protected filter = (item: any): boolean => {
        return (
            (!this.state.filter) ||
            (item.label.toLocaleLowerCase().indexOf(this.state.filter.toLocaleLowerCase()) !== -1)
        );
    };

    protected allowedCamera = (): boolean => {
        return (
            this.state.camera !== core.view.CameraMode.Back &&
            this.state.camera !== core.view.CameraMode.Front &&
            this.state.camera !== core.view.CameraMode.Left &&
            this.state.camera !== core.view.CameraMode.Right
        );
    };

    protected contentCSSClass = (hasInput: boolean): string => {
        const result: Array<string> = ['sidebar-content'];
        if (hasInput) {
            result.push('input-header');
        } else if (AppConfigurationStore.getState().floorplanAvailable) {
            result.push('button-header');
        } else {
            result.push('standard-header');
        }
        return result.join(' ');
    };

    protected drawWall = (event?: React.MouseEvent): void => {
        UIHelper.stopEvent(event);
        if (this.state.dragMode) {
            GlobalHelper.stopActiveTool();
        } else {
            ArchitectureActions.drawWall();
        }
    };

    protected drawWalkways = (event?: React.MouseEvent): void => {
        UIHelper.stopEvent(event);
        if (this.state.dragMode) {
            GlobalHelper.stopActiveTool();
        } else {
            ArchitectureActions.drawWalkways();
        }
    };

    protected setFilter = (value: string | number | null): void => {
        this.setState(jQuery.extend({}, this.state, {filter: value}));
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/sidebar/Architecture.tsx